import React from "react";

import Button from "@mui/material/Button";

import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";

import { rest } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import styles from "./__SignatureDetails.module.styl";

const getField = (row, camelName, snakeName) =>
  row?.[camelName] ?? row?.[snakeName] ?? "";

const formatDateTime = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.replace("T", " ").slice(0, 19);
  try { return new Date(value).toISOString().replace("T", " ").slice(0, 19); }
  catch { return String(value); }
};

const formatAdditionalValue = (value, displayType) => {
  if (value === null || value === undefined || value === "") return "";
  if (displayType === "datetime") return formatDateTime(value);
  if (displayType === "date" || displayType === "time") return String(value);
  if (displayType === "boolean") return value ? "Yes" : "No";
  if (displayType === "json") {
    try { return typeof value === "string" ? value : JSON.stringify(value); }
    catch { return String(value); }
  }
  return String(value);
};

const fullName = (row) => [
  getField(row, "firstName", "first_name"),
  getField(row, "lastName", "last_name")
].map((value) => String(value || "").trim()).filter(Boolean).join(" ");

const SectionCard = ({ icon: Icon, title, subtitle, children }) => (
  <section className={styles.card}>
    <header className={styles.cardHeader}>
      <div className={styles.cardTitleRow}>
        <div className={styles.cardIconWrap}><Icon /></div>
        <div>
          <h2 className={styles.cardTitle}>{title}</h2>
          {subtitle ? <div className={styles.cardSubtitle}>{subtitle}</div> : null}
        </div>
      </div>
    </header>
    <div className={styles.cardBody}>{children}</div>
  </section>
);

const Field = ({ label, value, children }) => {
  if ((value === null || value === undefined || value === "") && !children) return null;
  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{children || String(value)}</div>
    </div>
  );
};

const LinkedField = ({ gui, label, value, href }) => {
  if (!value || !href) return null;
  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{label}</div>
      <button type="button" className={styles.linkValue} onClick={() => gui.goTo(href)}>
        {String(value)}
      </button>
    </div>
  );
};

export const setOperatorUserEnabled = async (gid, enabled) => {
  if (gid === null || gid === undefined) {
    throw new Error("operator_user.gid is required");
  }

  return await rest.request(
    getServiceUri()
      + `operator-user/${Number(gid)}/${enabled ? "enable" : "disable"}`,
    "POST",
    null
  );
};

export const readOperatorUser = async (gid) => {
  const rows = await rest.request(getServiceUri() + "operator-user/search", "POST", {
    columns: [], query: `gid==${Number(gid)}`, limit: 2, offset: 0
  });
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (rows.length !== 1) throw new Error("Expected exactly one operator_user for gid " + gid);
  return rows[0];
};

export const OperatorUserDetailsWidget = ({
  gui,
  id
}) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setError("");
    const user = await readOperatorUser(id);
    setData(user);
  }, [id]);

  React.useEffect(() => {
    let cancelled = false;
    load().catch((err) => {
      if (!cancelled) setError(err?.message || String(err));
    });
    return () => { cancelled = true; };
  }, [load]);

  const changeEnabled = async (enabled) => {
    if (!data?.gid || saving) return;
    setSaving(true);
    setError("");
    try {
      await setOperatorUserEnabled(data.gid, enabled);
      await load();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return <div className={styles.loading}>Loading</div>;

  const name = fullName(data) || data.username || "Operator user";
  const approvalRequestGid = getField(data, "approvalRequestGid", "approval_request_gid");

  return (
    <div className={styles.page}>
      <section className={styles.header}>
        <div className={styles.headerMain}>
          <div className={styles.heroIconWrap}><AccountCircleOutlinedIcon className={styles.heroIcon} /></div>
          <div>
            <h1 className={styles.headerTitle}>{name}</h1>
            <div className={styles.headerSubtitle}>Operator user <span>#</span>{data.gid || id}</div>
          </div>
        </div>
        <div className={styles.headerActions}>
          {data.status ? <div className={styles.statusPill}>{data.status}</div> : null}
          {can(gui, "MANAGE_OPERATOR_USER") ? (
            String(data.status || "").toUpperCase() === "DISABLED" ? (
              <Button
                variant="outlined"
                startIcon={<CheckCircleOutlineIcon />}
                disabled={saving}
                onClick={() => changeEnabled(true)}
              >
                Enable
              </Button>
            ) : (
              <Button
                variant="outlined"
                startIcon={<BlockOutlinedIcon />}
                disabled={saving}
                onClick={() => changeEnabled(false)}
              >
                Disable
              </Button>
            )
          ) : null}
        </div>
      </section>

      <div className={styles.gridTwo}>
        <SectionCard icon={BadgeOutlinedIcon} title="Identity" subtitle="Signature-realm identity">
          <div className={styles.fieldsGrid}>
            <Field label="First name" value={getField(data, "firstName", "first_name")} />
            <Field label="Last name" value={getField(data, "lastName", "last_name")} />
            <Field label="Username" value={data.username} />
            <Field label="National ID" value={getField(data, "nationalId", "national_id")} />
            <Field label="Device ID" value={getField(data, "deviceId", "device_id")} />
          </div>
        </SectionCard>

        <SectionCard icon={ScheduleOutlinedIcon} title="Account" subtitle="Signature account state">
          <div className={styles.fieldsGrid}>
            <Field label="Status" value={data.status} />
            <Field label="Created at" value={formatDateTime(getField(data, "createdAt", "created_at"))} />
            <Field label="Updated at" value={formatDateTime(getField(data, "updatedAt", "updated_at"))} />
            <LinkedField
              gui={gui}
              label="Approval request"
              value={approvalRequestGid}
              href={approvalRequestGid ? `/operator-account-request/${approvalRequestGid}` : ""}
            />
          </div>
        </SectionCard>
      </div>

      <SectionCard icon={VpnKeyOutlinedIcon} title="Signature" subtitle="Public signing identity">
        <div className={styles.fieldsGrid}>
          <Field label="Public key" value={getField(data, "publicKey", "public_key")} />
        </div>
      </SectionCard>

      <SectionCard icon={BadgeOutlinedIcon} title="Additional information" subtitle="Operator account attributes">
        <div className={styles.fieldsGrid}>
          <Field
            label="Commune Gid"
            value={formatAdditionalValue(
              getField(data, "communeGid", "commune_gid"),
              "text"
            )}
          />
          <Field
            label="Department Gid"
            value={formatAdditionalValue(
              getField(data, "departmentGid", "department_gid"),
              "text"
            )}
          />
          <Field
            label="Operator Role"
            value={formatAdditionalValue(
              getField(data, "operatorRole", "operator_role"),
              "text"
            )}
          />
          <Field
            label="Voting Center Gid"
            value={formatAdditionalValue(
              getField(data, "votingCenterGid", "voting_center_gid"),
              "text"
            )}
          />
        </div>
      </SectionCard>
    </div>
  );
};

export default OperatorUserDetailsWidget;
